import React, { useState, useEffect, useCallback } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Collapse,
  Box,
  Typography,
  Select,
  MenuItem,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Snackbar,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  FormControlLabel,
  Checkbox,
  FormGroup,
  Divider,
  InputLabel,
  FormControl,
} from "@mui/material";
import {
  KeyboardArrowDown,
  KeyboardArrowUp,
  Edit as EditIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
} from "@mui/icons-material";
import { customersApi } from "../adapters/api";
import { subscribe, connectWebSocket, disconnectWebSocket } from "../adapters/ws";

// Verfuegbare Web-Versionen
const WEB_VERSIONS = [
  '1.0.0',
];

// Verfuegbare Module
const AVAILABLE_MODULES = ['m-data', 'm-service', 'm-convert', 'm-status'];

// Modul-Versionen
const MODULE_VERSIONS = {
  'm-data': ['1.0.0'],
  'm-service': ['1.0.0'],
  'm-convert': ['1.0.0'],
  'm-status': ['1.0.0'],
};

// Verfuegbare Web Tools
const AVAILABLE_WEB_TOOLS = ['ncViewer', '3dViewer', 'pdfViewer', 'markdownViewer'];

// Web Tool Versionen
const WEB_TOOL_VERSIONS = {
  'ncViewer': ['1.0.0'],
  '3dViewer': ['1.0.0'],
  'pdfViewer': ['1.0.0'],
  'markdownViewer': ['1.0.0'],
};

// Data Storage Optionen
const DATA_STORAGE_OPTIONS = ['hidrive', 's3ObjectStorage'];

// Login Optionen
const LOGIN_OPTIONS = ['fisquare'];

// Theme Optionen
const THEME_OPTIONS = ['standard_theme', 'jochen_theme'];

// ERP Systeme
const ERP_SYSTEMS = ['fisquare'];

// Expandable Customer Details Panel
const CustomerExpandPanel = ({ customer }) => {
  const panelStyle = {
    padding: '20px',
    backgroundColor: '#e3f2fd',
    margin: '10px',
    border: '1px solid #1976d2',
    borderRadius: '4px'
  };

  const headerStyle = {
    marginTop: 0,
    marginBottom: '15px',
    color: '#0d47a1',
    fontSize: '18px',
    fontWeight: 'bold'
  };

  const rowStyle = {
    marginBottom: '12px',
    color: '#000',
    fontSize: '14px'
  };

  const labelStyle = {
    color: '#0d47a1',
    fontWeight: 'bold',
    marginRight: '8px'
  };

  const boxStyle = {
    backgroundColor: '#fff',
    padding: '10px',
    borderRadius: '4px',
    marginTop: '4px',
    color: '#000',
    border: '1px solid #90caf9'
  };

  return (
    <div style={panelStyle}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '20px' }}>
        {/* Spalte 1: System-Informationen */}
        <div>
          <h3 style={headerStyle}>System-Informationen</h3>
          <div style={rowStyle}>
            <span style={labelStyle}>Instance Nr:</span>
            <span>{customer.instanceNr}</span>
          </div>
          <div style={rowStyle}>
            <span style={labelStyle}>Login:</span>
            <span>{customer.loginWith}</span>
          </div>
          <div style={rowStyle}>
            <span style={labelStyle}>Websocket URL:</span>
            <span>{customer.wsUrl}</span>
          </div>
          {customer.loginWithSettings && (
            <div style={rowStyle}>
              <div style={labelStyle}>Login Settings:</div>
              <pre style={{ ...boxStyle, fontSize: '12px', margin: 0, maxHeight: '120px', overflow: 'auto' }}>
                {JSON.stringify(customer.loginWithSettings, null, 2)}
              </pre>
            </div>
          )}
          <div style={rowStyle}>
            <span style={labelStyle}>Data Storage:</span>
            <span>{customer.dataWith}</span>
          </div>
          {customer.dataWithSettings && (
            <div style={rowStyle}>
              <div style={labelStyle}>Data Settings:</div>
              <pre style={{ ...boxStyle, fontSize: '12px', margin: 0, maxHeight: '120px', overflow: 'auto' }}>
                {JSON.stringify(
                  Object.fromEntries(
                    Object.entries(customer.dataWithSettings).map(([provider, settings]) => [
                      provider,
                      Object.fromEntries(
                        Object.entries(settings).filter(([key]) =>
                          !['username', 'password', 'clientSecret', 'redirectUri', 'accessToken', 'refreshToken'].includes(key)
                        )
                      )
                    ])
                  ),
                  null, 2
                )}
              </pre>
            </div>
          )}
          <div style={rowStyle}>
            <span style={labelStyle}>Max User Count:</span>
            <span>{customer.maxUserCount}</span>
          </div>
          <div style={rowStyle}>
            <span style={labelStyle}>Last Login:</span>
            <span>{customer.lastLogin ? new Date(customer.lastLogin).toLocaleString('de-DE') : 'N/A'}</span>
          </div>
          <div style={rowStyle}>
            <span style={labelStyle}>External API Version:</span>
            <span>{customer.externalApiVersion}</span>
          </div>
          <div style={rowStyle}>
            <span style={labelStyle}>Theme:</span>
            <span>{customer.theme}</span>
          </div>
        </div>

        {/* Spalte 2: Module & Tools */}
        <div>
          <h3 style={headerStyle}>Module & Tools</h3>
          {customer.modules && (
            <div style={rowStyle}>
              <div style={labelStyle}>Module:</div>
              <div style={boxStyle}>
                {customer.modules.join(', ')}
              </div>
            </div>
          )}
          {customer.moduleVersion && (
            <div style={rowStyle}>
              <div style={labelStyle}>Module Versionen:</div>
              <pre style={{ ...boxStyle, fontSize: '12px', margin: 0, maxHeight: '120px', overflow: 'auto' }}>
                {JSON.stringify(customer.moduleVersion, null, 2)}
              </pre>
            </div>
          )}
          {customer.webTools && (
            <div style={rowStyle}>
              <div style={labelStyle}>Web Tools:</div>
              <pre style={{ ...boxStyle, fontSize: '12px', margin: 0, maxHeight: '120px', overflow: 'auto' }}>
                {JSON.stringify(customer.webTools, null, 2)}
              </pre>
            </div>
          )}
          {customer.webToolsSettings && Object.keys(customer.webToolsSettings).length > 0 && (
            <div style={rowStyle}>
              <div style={labelStyle}>Web Tools Settings:</div>
              <pre style={{ ...boxStyle, fontSize: '12px', margin: 0, maxHeight: '120px', overflow: 'auto' }}>
                {JSON.stringify(customer.webToolsSettings, null, 2)}
              </pre>
            </div>
          )}
        </div>

        {/* Spalte 3: Module Settings */}
        <div>
          <h3 style={headerStyle}>Module Settings</h3>
          {customer.moduleSettings?.mDataSettings && Object.keys(customer.moduleSettings.mDataSettings).length > 0 && (
            <div style={rowStyle}>
              <div style={labelStyle}>mData Settings:</div>
              <pre style={{ ...boxStyle, fontSize: '11px', margin: 0, maxHeight: '120px', overflow: 'auto' }}>
                {JSON.stringify(customer.moduleSettings.mDataSettings, null, 2)}
              </pre>
            </div>
          )}
          {customer.moduleSettings?.mStatusSettings && Object.keys(customer.moduleSettings.mStatusSettings).length > 0 && (
            <div style={rowStyle}>
              <div style={labelStyle}>mStatus Settings:</div>
              <pre style={{ ...boxStyle, fontSize: '11px', margin: 0, maxHeight: '120px', overflow: 'auto' }}>
                {JSON.stringify(customer.moduleSettings.mStatusSettings, null, 2)}
              </pre>
            </div>
          )}
          {customer.moduleSettings?.mServiceSettings && Object.keys(customer.moduleSettings.mServiceSettings).length > 0 && (
            <div style={rowStyle}>
              <div style={labelStyle}>mService Settings:</div>
              <pre style={{ ...boxStyle, fontSize: '11px', margin: 0, maxHeight: '120px', overflow: 'auto' }}>
                {JSON.stringify(customer.moduleSettings.mServiceSettings, null, 2)}
              </pre>
            </div>
          )}
          {customer.moduleSettings?.mConvertSettings && Object.keys(customer.moduleSettings.mConvertSettings).length > 0 && (
            <div style={rowStyle}>
              <div style={labelStyle}>mConvert Settings:</div>
              <pre style={{ ...boxStyle, fontSize: '11px', margin: 0, maxHeight: '120px', overflow: 'auto' }}>
                {JSON.stringify(customer.moduleSettings.mConvertSettings, null, 2)}
              </pre>
            </div>
          )}
          {!customer.moduleSettings || (
            Object.keys(customer.moduleSettings?.mDataSettings || {}).length === 0 &&
            Object.keys(customer.moduleSettings?.mStatusSettings || {}).length === 0 &&
            Object.keys(customer.moduleSettings?.mServiceSettings || {}).length === 0 &&
            Object.keys(customer.moduleSettings?.mConvertSettings || {}).length === 0
          ) ? (
            <div style={{ ...boxStyle, color: '#666' }}>Keine Module Settings konfiguriert</div>
          ) : null}
        </div>
      </div>

    </div>
  );
};

// Customer Row Component
const CustomerRow = ({ customer, onVersionChange, onEdit, onDelete }) => {
  const [open, setOpen] = useState(false);

  const handleVersionChange = (event) => {
    event.stopPropagation();
    onVersionChange(customer.id, event.target.value);
  };

  return (
    <>
      <TableRow
        sx={{ '& > *': { borderBottom: 'unset' }, cursor: 'pointer' }}
        onClick={() => setOpen(!open)}
        hover
      >
        <TableCell>
          <IconButton size="small">
            {open ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
          </IconButton>
        </TableCell>
        <TableCell>{customer.customerId}</TableCell>
        <TableCell>{customer.name}</TableCell>
        <TableCell onClick={(e) => e.stopPropagation()}>
          <Select
            value={customer.webVersion || ''}
            onChange={handleVersionChange}
            size="small"
            sx={{ minWidth: 150 }}
          >
            {WEB_VERSIONS.map((ver) => (
              <MenuItem key={ver} value={ver}>
                {ver}
              </MenuItem>
            ))}
          </Select>
        </TableCell>
        <TableCell>{customer.erpSystem}</TableCell>
        <TableCell>
          {customer.createdAt ? new Date(customer.createdAt).toLocaleString('de-DE') : 'N/A'}
        </TableCell>
        <TableCell onClick={(e) => e.stopPropagation()}>
          <IconButton size="small" onClick={() => onEdit(customer)}>
            <EditIcon />
          </IconButton>
          <IconButton
            size="small"
            onClick={() => onDelete(customer)}
            sx={{ color: 'error.main', '&:hover': { backgroundColor: 'error.light', color: 'white' } }}
          >
            <DeleteIcon />
          </IconButton>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={7}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <CustomerExpandPanel customer={customer} />
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

// Tab Panel Component
const TabPanel = ({ children, value, index }) => (
  <div hidden={value !== index} style={{ paddingTop: 16 }}>
    {value === index && children}
  </div>
);

// Customer Form Dialog - Erweitert mit allen Feldern
const CustomerFormDialog = ({ open, onClose, customer, onSave, isSaving }) => {
  const [tabIndex, setTabIndex] = useState(0);
  // Module Settings States
  const [mDataSettingsText, setMDataSettingsText] = useState('{}');
  const [mDataSettingsError, setMDataSettingsError] = useState('');
  const [mStatusSettingsText, setMStatusSettingsText] = useState('{}');
  const [mStatusSettingsError, setMStatusSettingsError] = useState('');
  const [mServiceSettingsText, setMServiceSettingsText] = useState('{}');
  const [mServiceSettingsError, setMServiceSettingsError] = useState('');
  const [mConvertSettingsText, setMConvertSettingsText] = useState('{}');
  const [mConvertSettingsError, setMConvertSettingsError] = useState('');
  // Meta Data State
  const [metaDataText, setMetaDataText] = useState('{}');
  const [metaDataError, setMetaDataError] = useState('');
  // Permissions State
  const [permissionsText, setPermissionsText] = useState('{}');
  const [permissionsError, setPermissionsError] = useState('');
  const [formData, setFormData] = useState({
    // Grunddaten
    name: '',
    customerId: '',
    email: '',
    phone: '',
    // System
    instanceNr: 1,
    webVersion: '',
    externalApiVersion: '',
    wsUrl: '',
    erpSystem: '',
    theme: 'standard_theme',
    maxUserCount: 10,
    // Login
    loginWith: 'fisquare',
    loginWithSettings: {
      fisquare: { apiKey: '', apiUrl: 'https://api.fisquare.com/' }
    },
    // Data Storage
    dataWith: 'hidrive',
    dataWithSettings: {
      hidrive: { accessToken: '', refreshToken: '' },
      s3ObjectStorage: { bucketName: '', region: 'eu-central-1', accessKey: '' }
    },
    // Module
    modules: [],
    moduleVersion: {},
    // Web Tools
    webTools: {},
    // Module Settings (übergeordnet)
    moduleSettings: {
      mDataSettings: {},
      mStatusSettings: {},
      mServiceSettings: {},
      mConvertSettings: {},
    },
    // Meta Data
    metaData: {},
    // Permissions
    permissions: {},
  });

  useEffect(() => {
    if (customer) {
      setFormData({
        name: customer.name || '',
        customerId: customer.customerId || '',
        email: customer.email || '',
        phone: customer.phone || '',
        instanceNr: customer.instanceNr || 1,
        webVersion: customer.webVersion || '',
        externalApiVersion: customer.externalApiVersion || '',
        wsUrl: customer.wsUrl || '',
        erpSystem: customer.erpSystem || '',
        theme: customer.theme || 'standard_theme',
        maxUserCount: customer.maxUserCount || 10,
        loginWith: customer.loginWith || 'fisquare',
        loginWithSettings: customer.loginWithSettings || {
          fisquare: { apiKey: '', apiUrl: 'https://api.fisquare.com/' }
        },
        dataWith: customer.dataWith || 'hidrive',
        dataWithSettings: {
          hidrive: customer.dataWithSettings?.hidrive || { accessToken: '', refreshToken: '' },
          s3ObjectStorage: customer.dataWithSettings?.s3ObjectStorage || { bucketName: '', region: 'eu-central-1', accessKey: '' }
        },
        modules: customer.modules || [],
        moduleVersion: customer.moduleVersion || {},
        webTools: customer.webTools || {},
        moduleSettings: {
          mDataSettings: customer.moduleSettings?.mDataSettings || {},
          mStatusSettings: customer.moduleSettings?.mStatusSettings || {},
          mServiceSettings: customer.moduleSettings?.mServiceSettings || {},
          mConvertSettings: customer.moduleSettings?.mConvertSettings || {},
        },
        metaData: customer.metaData || {},
        permissions: customer.permissions || {},
      });
      // Module Settings Text
      setMDataSettingsText(JSON.stringify(customer.moduleSettings?.mDataSettings || {}, null, 2));
      setMDataSettingsError('');
      setMStatusSettingsText(JSON.stringify(customer.moduleSettings?.mStatusSettings || {}, null, 2));
      setMStatusSettingsError('');
      setMServiceSettingsText(JSON.stringify(customer.moduleSettings?.mServiceSettings || {}, null, 2));
      setMServiceSettingsError('');
      setMConvertSettingsText(JSON.stringify(customer.moduleSettings?.mConvertSettings || {}, null, 2));
      setMConvertSettingsError('');
      // Meta Data Text
      setMetaDataText(JSON.stringify(customer.metaData || {}, null, 2));
      setMetaDataError('');
      // Permissions Text
      setPermissionsText(JSON.stringify(customer.permissions || {}, null, 2));
      setPermissionsError('');
    } else {
      setFormData({
        name: '',
        customerId: '',
        email: '',
        phone: '',
        instanceNr: 1,
        webVersion: '1.3-Stable',
        externalApiVersion: '2.1',
        wsUrl: '',
        erpSystem: 'SAP',
        theme: 'standard_theme',
        maxUserCount: 10,
        loginWith: 'fisquare',
        loginWithSettings: {
          fisquare: { apiKey: '', apiUrl: 'https://api.fisquare.com/' }
        },
        dataWith: 'hidrive',
        dataWithSettings: {
          hidrive: { accessToken: '', refreshToken: '' },
          s3ObjectStorage: { bucketName: '', region: 'eu-central-1', accessKey: '' }
        },
        modules: ['m-data'],
        moduleVersion: { 'm-data': '1.3.1-Debug' },
        webTools: { pdfViewer: '1.8.3' },
        moduleSettings: {
          mDataSettings: {},
          mStatusSettings: {},
          mServiceSettings: {},
          mConvertSettings: {},
        },
        metaData: {},
        permissions: {},
      });
      // Reset all JSON text states
      setMDataSettingsText('{}');
      setMDataSettingsError('');
      setMStatusSettingsText('{}');
      setMStatusSettingsError('');
      setMServiceSettingsText('{}');
      setMServiceSettingsError('');
      setMConvertSettingsText('{}');
      setMConvertSettingsError('');
      setMetaDataText('{}');
      setMetaDataError('');
      setPermissionsText('{}');
      setPermissionsError('');
    }
    setTabIndex(0);
  }, [customer, open]);

  const handleChange = (field) => (event) => {
    setFormData({ ...formData, [field]: event.target.value });
  };

  const handleModuleToggle = (moduleName) => {
    const newModules = formData.modules.includes(moduleName)
      ? formData.modules.filter(m => m !== moduleName)
      : [...formData.modules, moduleName];

    const newModuleVersion = { ...formData.moduleVersion };
    if (!formData.modules.includes(moduleName)) {
      // Setze die neueste stabile Version als Default
      const versions = MODULE_VERSIONS[moduleName] || [];
      const stableVersions = versions.filter(v => v.includes('Stable'));
      newModuleVersion[moduleName] = stableVersions[stableVersions.length - 1] || versions[0] || '1.0.0-Stable';
    } else {
      delete newModuleVersion[moduleName];
    }

    setFormData({ ...formData, modules: newModules, moduleVersion: newModuleVersion });
  };

  const handleModuleVersionChange = (moduleName, version) => {
    setFormData({
      ...formData,
      moduleVersion: { ...formData.moduleVersion, [moduleName]: version }
    });
  };

  const handleWebToolToggle = (toolName) => {
    const newWebTools = { ...formData.webTools };
    if (newWebTools[toolName]) {
      delete newWebTools[toolName];
    } else {
      // Setze die neueste Version als Default (ohne Beta/Alpha)
      const versions = WEB_TOOL_VERSIONS[toolName] || [];
      const stableVersions = versions.filter(v => !v.includes('Beta') && !v.includes('Alpha'));
      newWebTools[toolName] = stableVersions[stableVersions.length - 1] || versions[0] || '1.0.0';
    }
    setFormData({ ...formData, webTools: newWebTools });
  };

  const handleWebToolVersionChange = (toolName, version) => {
    setFormData({
      ...formData,
      webTools: { ...formData.webTools, [toolName]: version }
    });
  };

  const handleLoginSettingChange = (field, value) => {
    setFormData({
      ...formData,
      loginWithSettings: {
        ...formData.loginWithSettings,
        fisquare: { ...formData.loginWithSettings.fisquare, [field]: value }
      }
    });
  };

  const handleDataSettingChange = (storageType, field, value) => {
    setFormData({
      ...formData,
      dataWithSettings: {
        ...formData.dataWithSettings,
        [storageType]: { ...formData.dataWithSettings[storageType], [field]: value }
      }
    });
  };

  const handleSubmit = (keepOpen = true) => {
    if (isSaving) return;
    // Validierung aller JSON-Felder
    if (mDataSettingsError || mStatusSettingsError || mServiceSettingsError || mConvertSettingsError || metaDataError || permissionsError) return;
    const dataToSave = {
      ...formData,
      dataWithSettings: {
        [formData.dataWith]: formData.dataWithSettings[formData.dataWith]
      },
      moduleSettings: formData.moduleSettings,
      metaData: formData.metaData,
      permissions: formData.permissions,
    };
    onSave(dataToSave, { keepOpen });
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        onKeyDown: (e) => {
          if ((e.ctrlKey || e.metaKey) && e.key === 's') {
            e.preventDefault();
            handleSubmit(true);
          }
        }
      }}
    >
      <DialogTitle>{customer ? 'Kunde bearbeiten' : 'Neuer Kunde'}</DialogTitle>
      <DialogContent>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={tabIndex} onChange={(e, v) => setTabIndex(v)}>
            <Tab label="Grunddaten" />
            <Tab label="System" />
            <Tab label="Storage" />
            <Tab label="Module" />
            <Tab label="Web Tools" />
            <Tab label="Module Settings" />
          </Tabs>
        </Box>

        {/* Tab 0: Grunddaten */}
        <TabPanel value={tabIndex} index={0}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Kundenname"
              value={formData.name}
              onChange={handleChange('name')}
              fullWidth
              required
            />
            <TextField
              label="Customer ID"
              value={formData.customerId}
              onChange={handleChange('customerId')}
              fullWidth
              required
            />
            <TextField
              label="Email"
              value={formData.email}
              onChange={handleChange('email')}
              fullWidth
              type="email"
            />
            <TextField
              label="Telefon"
              value={formData.phone}
              onChange={handleChange('phone')}
              fullWidth
            />
            <FormControl fullWidth>
              <InputLabel>ERP System</InputLabel>
              <Select
                value={formData.erpSystem}
                onChange={handleChange('erpSystem')}
                label="ERP System"
              >
                {ERP_SYSTEMS.map(erp => (
                  <MenuItem key={erp} value={erp}>{erp}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </TabPanel>

        {/* Tab 1: System */}
        <TabPanel value={tabIndex} index={1}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <FormControl fullWidth>
              <InputLabel>Web Version</InputLabel>
              <Select
                value={formData.webVersion}
                onChange={handleChange('webVersion')}
                label="Web Version"
              >
                {WEB_VERSIONS.map(ver => (
                  <MenuItem key={ver} value={ver}>{ver}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="External API Version"
              value={formData.externalApiVersion}
              onChange={handleChange('externalApiVersion')}
              fullWidth
            />
            <TextField
              label="Websocket URL"
              value={formData.wsUrl}
              onChange={handleChange('wsUrl')}
              fullWidth
            />
            <TextField
              label="Max User Count"
              value={formData.maxUserCount}
              onChange={(e) => setFormData({ ...formData, maxUserCount: parseInt(e.target.value) || 1 })}
              fullWidth
              type="number"
            />
            <FormControl fullWidth>
              <InputLabel>Theme</InputLabel>
              <Select
                value={formData.theme}
                onChange={handleChange('theme')}
                label="Theme"
              >
                {THEME_OPTIONS.map(theme => (
                  <MenuItem key={theme} value={theme}>{theme}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </TabPanel>

        {/* Tab 2: Storage */}
        <TabPanel value={tabIndex} index={2}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>Login Provider</Typography>
            <FormControl fullWidth>
              <InputLabel>Login mit</InputLabel>
              <Select
                value={formData.loginWith}
                onChange={handleChange('loginWith')}
                label="Login mit"
              >
                {LOGIN_OPTIONS.map(opt => (
                  <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Fisquare API Key"
              value={formData.loginWithSettings?.fisquare?.apiKey || ''}
              onChange={(e) => handleLoginSettingChange('apiKey', e.target.value)}
              fullWidth
            />
            <TextField
              label="Fisquare API URL"
              value={formData.loginWithSettings?.fisquare?.apiUrl || ''}
              onChange={(e) => handleLoginSettingChange('apiUrl', e.target.value)}
              fullWidth
            />

            <Divider sx={{ my: 2 }} />

            <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>Data Storage</Typography>
            <FormControl fullWidth>
              <InputLabel>Data Storage</InputLabel>
              <Select
                value={formData.dataWith}
                onChange={handleChange('dataWith')}
                label="Data Storage"
              >
                {DATA_STORAGE_OPTIONS.map(opt => (
                  <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                ))}
              </Select>
            </FormControl>

            {formData.dataWith === 'hidrive' && (
              <>
                <TextField
                  label="ACCESS_TOKEN"
                  value={formData.dataWithSettings?.hidrive?.accessToken || ''}
                  onChange={(e) => handleDataSettingChange('hidrive', 'accessToken', e.target.value)}
                  fullWidth
                  type="password"
                />
                <TextField
                  label="REFRESH_TOKEN"
                  value={formData.dataWithSettings?.hidrive?.refreshToken || ''}
                  onChange={(e) => handleDataSettingChange('hidrive', 'refreshToken', e.target.value)}
                  fullWidth
                  type="password"
                />
              </>
            )}

            {formData.dataWith === 's3ObjectStorage' && (
              <>
                <TextField
                  label="S3 Bucket Name"
                  value={formData.dataWithSettings?.s3ObjectStorage?.bucketName || ''}
                  onChange={(e) => handleDataSettingChange('s3ObjectStorage', 'bucketName', e.target.value)}
                  fullWidth
                />
                <TextField
                  label="S3 Region"
                  value={formData.dataWithSettings?.s3ObjectStorage?.region || ''}
                  onChange={(e) => handleDataSettingChange('s3ObjectStorage', 'region', e.target.value)}
                  fullWidth
                  placeholder="eu-central-1"
                />
                <TextField
                  label="S3 Access Key"
                  value={formData.dataWithSettings?.s3ObjectStorage?.accessKey || ''}
                  onChange={(e) => handleDataSettingChange('s3ObjectStorage', 'accessKey', e.target.value)}
                  fullWidth
                />
              </>
            )}
          </Box>
        </TabPanel>

        {/* Tab 3: Module */}
        <TabPanel value={tabIndex} index={3}>
          <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2 }}>Aktive Module</Typography>
          <FormGroup>
            {AVAILABLE_MODULES.map(mod => (
              <Box key={mod} sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.modules.includes(mod)}
                      onChange={() => handleModuleToggle(mod)}
                    />
                  }
                  label={mod}
                  sx={{ minWidth: 150 }}
                />
                {formData.modules.includes(mod) && (
                  <FormControl size="small" sx={{ minWidth: 180 }}>
                    <InputLabel>{mod} Version</InputLabel>
                    <Select
                      value={formData.moduleVersion[mod] || ''}
                      onChange={(e) => handleModuleVersionChange(mod, e.target.value)}
                      label={`${mod} Version`}
                    >
                      {MODULE_VERSIONS[mod]?.map(ver => (
                        <MenuItem key={ver} value={ver}>{ver}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              </Box>
            ))}
          </FormGroup>
        </TabPanel>

        {/* Tab 4: Web Tools */}
        <TabPanel value={tabIndex} index={4}>
          <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2 }}>Aktive Web Tools</Typography>
          <FormGroup>
            {AVAILABLE_WEB_TOOLS.map(tool => (
              <Box key={tool} sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={!!formData.webTools[tool]}
                      onChange={() => handleWebToolToggle(tool)}
                    />
                  }
                  label={tool}
                  sx={{ minWidth: 180 }}
                />
                {formData.webTools[tool] && (
                  <FormControl size="small" sx={{ minWidth: 150 }}>
                    <InputLabel>{tool} Version</InputLabel>
                    <Select
                      value={formData.webTools[tool] || ''}
                      onChange={(e) => handleWebToolVersionChange(tool, e.target.value)}
                      label={`${tool} Version`}
                    >
                      {WEB_TOOL_VERSIONS[tool]?.map(ver => (
                        <MenuItem key={ver} value={ver}>{ver}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              </Box>
            ))}
          </FormGroup>
        </TabPanel>

        {/* Tab 5: Module Settings */}
        <TabPanel value={tabIndex} index={5}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {/* mDataSettings */}
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>mDataSettings (JSON)</Typography>
              <TextField
                multiline
                minRows={12}
                maxRows={20}
                value={mDataSettingsText}
                onChange={(e) => {
                  const text = e.target.value;
                  setMDataSettingsText(text);
                  try {
                    const parsed = JSON.parse(text);
                    setFormData((prev) => ({
                      ...prev,
                      moduleSettings: { ...prev.moduleSettings, mDataSettings: parsed }
                    }));
                    setMDataSettingsError('');
                  } catch (err) {
                    setMDataSettingsError('Ungültiges JSON: ' + err.message);
                  }
                }}
                fullWidth
                error={!!mDataSettingsError}
                helperText={mDataSettingsError}
                InputProps={{
                  sx: { fontFamily: 'monospace', fontSize: '13px' }
                }}
              />
            </Box>

            <Divider />

            {/* mStatusSettings */}
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>mStatusSettings (JSON)</Typography>
              <TextField
                multiline
                minRows={12}
                maxRows={20}
                value={mStatusSettingsText}
                onChange={(e) => {
                  const text = e.target.value;
                  setMStatusSettingsText(text);
                  try {
                    const parsed = JSON.parse(text);
                    setFormData((prev) => ({
                      ...prev,
                      moduleSettings: { ...prev.moduleSettings, mStatusSettings: parsed }
                    }));
                    setMStatusSettingsError('');
                  } catch (err) {
                    setMStatusSettingsError('Ungültiges JSON: ' + err.message);
                  }
                }}
                fullWidth
                error={!!mStatusSettingsError}
                helperText={mStatusSettingsError}
                InputProps={{
                  sx: { fontFamily: 'monospace', fontSize: '13px' }
                }}
              />
            </Box>

            <Divider />

            {/* mServiceSettings */}
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>mServiceSettings (JSON)</Typography>
              <TextField
                multiline
                minRows={12}
                maxRows={20}
                value={mServiceSettingsText}
                onChange={(e) => {
                  const text = e.target.value;
                  setMServiceSettingsText(text);
                  try {
                    const parsed = JSON.parse(text);
                    setFormData((prev) => ({
                      ...prev,
                      moduleSettings: { ...prev.moduleSettings, mServiceSettings: parsed }
                    }));
                    setMServiceSettingsError('');
                  } catch (err) {
                    setMServiceSettingsError('Ungültiges JSON: ' + err.message);
                  }
                }}
                fullWidth
                error={!!mServiceSettingsError}
                helperText={mServiceSettingsError}
                InputProps={{
                  sx: { fontFamily: 'monospace', fontSize: '13px' }
                }}
              />
            </Box>

            <Divider />

            {/* mConvertSettings */}
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>mConvertSettings (JSON)</Typography>
              <TextField
                multiline
                minRows={12}
                maxRows={20}
                value={mConvertSettingsText}
                onChange={(e) => {
                  const text = e.target.value;
                  setMConvertSettingsText(text);
                  try {
                    const parsed = JSON.parse(text);
                    setFormData((prev) => ({
                      ...prev,
                      moduleSettings: { ...prev.moduleSettings, mConvertSettings: parsed }
                    }));
                    setMConvertSettingsError('');
                  } catch (err) {
                    setMConvertSettingsError('Ungültiges JSON: ' + err.message);
                  }
                }}
                fullWidth
                error={!!mConvertSettingsError}
                helperText={mConvertSettingsError}
                InputProps={{
                  sx: { fontFamily: 'monospace', fontSize: '13px' }
                }}
              />
            </Box>
          </Box>
        </TabPanel>

      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isSaving}>Abbrechen</Button>
        <Button onClick={() => handleSubmit(true)} variant="contained" disabled={isSaving}>
          Speichern
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Delete Confirmation Dialog with extra security
const DeleteConfirmDialog = ({ open, onClose, customer, onConfirm }) => {
  const [confirmText, setConfirmText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);

  const customerName = customer?.name || '';
  const isConfirmValid = confirmText === customerName;

  const handleConfirm = async () => {
    if (!isConfirmValid) return;
    setIsDeleting(true);
    await onConfirm(customer.id);
    setIsDeleting(false);
    setConfirmText('');
    onClose();
  };

  const handleClose = () => {
    setConfirmText('');
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ backgroundColor: 'error.main', color: 'white' }}>
        Kunde unwiderruflich loeschen
      </DialogTitle>
      <DialogContent sx={{ pt: 3 }}>
        <Alert severity="error" sx={{ mb: 3 }}>
          <strong>Achtung!</strong> Diese Aktion kann nicht rueckgaengig gemacht werden.
          Alle Daten des Kunden werden permanent geloescht.
        </Alert>

        <Box sx={{ mb: 3, p: 2, backgroundColor: 'grey.100', borderRadius: 1 }}>
          <Typography variant="body2" color="text.secondary">Kunde:</Typography>
          <Typography variant="h6">{customer?.name}</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>Customer ID:</Typography>
          <Typography variant="body1">{customer?.customerId}</Typography>
        </Box>

        <Typography variant="body2" sx={{ mb: 1 }}>
          Bitte geben Sie zur Bestaetigung den Kundennamen ein:
        </Typography>
        <Typography variant="body2" sx={{ mb: 2, fontWeight: 'bold', fontFamily: 'monospace' }}>
          {customerName}
        </Typography>

        <TextField
          fullWidth
          value={confirmText}
          onChange={(e) => setConfirmText(e.target.value)}
          placeholder="Kundennamen eingeben"
          error={confirmText.length > 0 && !isConfirmValid}
          helperText={confirmText.length > 0 && !isConfirmValid ? 'Der Name stimmt nicht ueberein' : ''}
          disabled={isDeleting}
          autoFocus
        />
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={handleClose} disabled={isDeleting}>
          Abbrechen
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          color="error"
          disabled={!isConfirmValid || isDeleting}
          startIcon={isDeleting ? <CircularProgress size={20} color="inherit" /> : <DeleteIcon />}
        >
          {isDeleting ? 'Wird geloescht...' : 'Endgueltig loeschen'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Main CustomersTable Component
const CustomersTable = () => {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editCustomer, setEditCustomer] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [customerToDelete, setCustomerToDelete] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const loadCustomers = useCallback(async () => {
    try {
      const data = await customersApi.getList();
      setCustomers(data);
    } catch (error) {
      console.error('Fehler beim Laden der Kunden:', error);
      setSnackbar({ open: true, message: 'Fehler beim Laden der Kunden', severity: 'error' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCustomers();

    // WebSocket für Echtzeit-Updates
    //connectWebSocket();
    const unsubscribe = subscribe((data) => {
      if (['customer_added', 'customer_updated', 'customer_deleted'].includes(data.type)) {
        console.log('Refreshing table due to WS event:', data.type);
        loadCustomers();
      }
    });

    return () => {
      unsubscribe();
      //disconnectWebSocket();
    };
  }, [loadCustomers]);

  const handleVersionChange = async (id, newVersion) => {
    try {
      const customer = customers.find(c => c.id === id);
      await customersApi.update(id, { ...customer, webVersion: newVersion });
      setSnackbar({ open: true, message: 'Web Version aktualisiert', severity: 'success' });
      loadCustomers();
    } catch (error) {
      console.error('Fehler beim Aktualisieren:', error);
      setSnackbar({ open: true, message: 'Fehler beim Aktualisieren', severity: 'error' });
    }
  };

  const handleEdit = (customer) => {
    setEditCustomer(customer);
    setDialogOpen(true);
  };

  const handleCreate = () => {
    setEditCustomer(null);
    setDialogOpen(true);
  };

  const handleSave = async (formData, { keepOpen = false } = {}) => {
    if (isSaving) return;
    setIsSaving(true);
    try {
      if (editCustomer) {
        await customersApi.update(editCustomer.id, { ...editCustomer, ...formData });
        setSnackbar({ open: true, message: 'Kunde aktualisiert', severity: 'success' });
      } else {
        const created = await customersApi.create(formData);
        // Dialog bleibt bei keepOpen offen -> ab jetzt aktualisieren statt erneut anzulegen
        setEditCustomer(created);
        setSnackbar({ open: true, message: 'Kunde erstellt', severity: 'success' });
      }
      if (!keepOpen) setDialogOpen(false);
      loadCustomers();
    } catch (error) {
      console.error('Fehler beim Speichern:', error);
      setSnackbar({ open: true, message: 'Fehler beim Speichern', severity: 'error' });
    } finally {
      setIsSaving(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleDelete = (customer) => {
    setCustomerToDelete(customer);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async (id) => {
    try {
      await customersApi.delete(id);
      setSnackbar({ open: true, message: 'Kunde erfolgreich geloescht', severity: 'success' });
      loadCustomers();
    } catch (error) {
      console.error('Fehler beim Loeschen:', error);
      setSnackbar({ open: true, message: 'Fehler beim Loeschen des Kunden', severity: 'error' });
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h5">Kunden</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleCreate}
        >
          Neuer Kunde
        </Button>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell width={50} />
              <TableCell>Customer ID</TableCell>
              <TableCell>Kundenname</TableCell>
              <TableCell>Web Version</TableCell>
              <TableCell>ERP System</TableCell>
              <TableCell>Erstellt am</TableCell>
              <TableCell width={80}>Aktionen</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {customers.map((customer) => (
              <CustomerRow
                key={customer.id}
                customer={customer}
                onVersionChange={handleVersionChange}
                onEdit={handleEdit}
                onDelete={handleDelete}
              />
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <CustomerFormDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        customer={editCustomer}
        onSave={handleSave}
        isSaving={isSaving}
      />

      <DeleteConfirmDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        customer={customerToDelete}
        onConfirm={handleConfirmDelete}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CustomersTable;
